import React, { useEffect, useRef, useState } from "react";
import { Typography, Box, Card, Grid, Divider } from "@mui/material";
import { theme } from "../services/Theme";
import { Storage } from "aws-amplify";
import SEO from "../components/SEO";

const aboutImageKeys = ["about/DSM.png", "about/DSMFrontHoodOpen.png"];

function AboutPage() {
  const [aboutImages, setAboutImages] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    async function fetchAboutImages() {
      try {
        const resolvedImages = await Promise.all(
          aboutImageKeys.map((key) => Storage.get(key, { level: "public" })),
        );

        setAboutImages(resolvedImages);
      } catch (error) {
        console.error("Unable to load About page images from S3", error);
      }
    }

    fetchAboutImages();
  }, []);

  useEffect(() => {
    if (!sectionRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.18,
      },
    );

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Box
      ref={sectionRef}
      sx={{
        width: "100%",
        maxWidth: "1200px",
        mx: "auto",
        pt: { xs: 0.5, sm: 1 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <SEO
        title="About Us"
        description="Learn about HaasFab, an Ohio-based fabrication and finishing shop specializing in powder coating, Cerakote, welding, and custom metalwork."
        path="/about"
      />
      <Grid container spacing={{ xs: 1.5, sm: 3 }} alignItems={{ xs: "stretch", md: "start" }}>
        {aboutImageKeys.map((key, index) => (
          <Grid
            key={key}
            item
            xs={12}
            sm={6}
            sx={{ order: { xs: index === 0 ? 1 : 3, sm: index + 1 } }}
          >
            <Box
              sx={{
                width: "100%",
                aspectRatio: "1 / 1",
                borderRadius: 2,
                backgroundImage: aboutImages[index] ? `url(${aboutImages[index]})` : "none",
                backgroundPosition: "center",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundColor: theme.palette.primary.main,
                boxShadow: "0 14px 32px rgba(0,0,0,0.18)",
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0) scale(1)" : "translateY(24px) scale(0.985)",
                transition: "opacity 520ms ease, transform 520ms ease",
                transitionDelay: `${index * 110}ms`,
              }}
            />
          </Grid>
        ))}

        <Grid item xs={12} sx={{ order: { xs: 2, sm: 3 } }}>
          <Card
            sx={{
              height: "100%",
              minHeight: "auto",
              p: { xs: 2, sm: 2.5, md: 3 },
              borderRadius: 2,
              backgroundColor: theme.palette.background.main,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(16px)",
              transition: "opacity 580ms ease, transform 580ms ease",
              transitionDelay: "220ms",
            }}
          >
            <Typography
              textAlign="center"
              sx={{
                color: theme.palette.text.light,
                fontSize: { xs: "0.8rem", sm: "0.86rem" },
                letterSpacing: 1.6,
                textTransform: "uppercase",
                opacity: 0.8,
                mb: 0.65,
              }}
            >
              About Us
            </Typography>

            <Typography
              textAlign="center"
              sx={{
                color: theme.palette.text.light,
                fontSize: { xs: "1.3rem", sm: "1.6rem", md: "1.85rem" },
                fontWeight: 700,
                lineHeight: 1.18,
              }}
            >
              Built From a Restoration That Refused to Be Scrapped
            </Typography>

            <Divider sx={{ my: 1.4, borderColor: theme.palette.divider }} />

            <Box sx={{ display: "grid", gap: 1.05 }}>
              <Typography
                sx={{
                  color: theme.palette.text.light,
                  fontSize: { xs: "0.88rem", sm: "0.95rem" },
                  lineHeight: 1.55,
                }}
              >
                HaasFab started with a 1995 Mitsubishi Eclipse GSX that most people would have scrapped.
                Severe rust in the rocker panels and strut towers required a full teardown, cutting out panels and rust, fabricating replacement sections,
                and a careful welding process to bring it back.
              </Typography>

              <Typography
                sx={{
                  color: theme.palette.text.light,
                  fontSize: { xs: "0.88rem", sm: "0.95rem" },
                  lineHeight: 1.55,
                }}
              >
                What began as "small" repair became full restoration: suspension and drivetrain work, engine maintenance and modification,
                interior and floor refinishing, and many upgrades to modernize the car. TIG welding for aluminum and
                stainless became essential for parts like the full exhaust, valve cover, and intake piping.
              </Typography>

              <Typography
                sx={{
                  color: theme.palette.text.light,
                  fontSize: { xs: "0.88rem", sm: "0.95rem" },
                  lineHeight: 1.55,
                }}
              >
                Powder coating and Cerakote were adopted to improve finish quality and longevity. A 3D printer
                and CNC plasma table expanded what we could build, from custom bracketry and repair patches to
                interior pieces, trim, and one-off functional components.
              </Typography>

              <Typography
                sx={{
                  color: theme.palette.text.light,
                  fontSize: { xs: "0.88rem", sm: "0.95rem" },
                  lineHeight: 1.55,
                }}
              >
                Those fabrication needs pushed us into CAD modeling, and over time that evolved into more complex,
                production-ready designs. We also share many of these designs with the DSM community to help keep
                the platform alive.
              </Typography>

              <Typography
                sx={{
                  color: theme.palette.text.light,
                  fontSize: { xs: "0.88rem", sm: "0.95rem" },
                  lineHeight: 1.55,
                }}
              >
                Based in Bowling Green, Ohio, we serve customers throughout Northwest Ohio — including Toledo,
                Perrysburg, Maumee, Findlay, Fremont, and surrounding communities.
              </Typography>
            </Box>

            <Typography
              sx={{
                color: theme.palette.text.light,
                fontSize: { xs: "0.84rem", sm: "0.9rem" },
                lineHeight: 1.5,
                fontStyle: "italic",
                opacity: 0.9,
                mt: 1.35,
                pt: 1.3,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              The car is never really finished, and that mindset became the foundation of HaasFab: improve
              year after year and help others do the same.
            </Typography>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AboutPage;
